import React, { useState, useEffect } from 'react';
import { useNotificationPermission } from '../hooks/useNotificationPermission';
import { usePWA } from '../hooks/usePWA';
import { Bell, BellOff, CheckCircle, AlertTriangle, RefreshCw } from 'lucide-react';

interface PushNotificationStatusProps {
  showDetails?: boolean;
  className?: string;
}

export const PushNotificationStatus: React.FC<PushNotificationStatusProps> = ({ 
  showDetails = false, 
  className = '' 
}) => {
  const { 
    permission, 
    isSupported, 
    isPushSupported, 
    hasActiveSubscription,
    needsAttention,
    refreshStatus 
  } = useNotificationPermission();
  
  const { pushSubscription } = usePWA();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshStatus();
    } finally {
      setIsRefreshing(false);
    }
  };

  const getStatusInfo = () => {
    if (!isSupported) {
      return {
        icon: <BellOff className="h-5 w-5 text-red-500" />,
        text: 'Não Suportado',
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200'
      };
    }

    if (permission === 'denied') {
      return {
        icon: <BellOff className="h-5 w-5 text-red-500" />,
        text: 'Permissão Negada',
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200'
      };
    }

    if (permission === 'granted' && hasActiveSubscription) {
      return {
        icon: <CheckCircle className="h-5 w-5 text-green-500" />,
        text: 'Ativado e Funcionando',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200'
      };
    }

    if (permission === 'granted' && !hasActiveSubscription) {
      return {
        icon: <AlertTriangle className="h-5 w-5 text-yellow-500" />,
        text: 'Permissão Concedida - Configurando...',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200'
      };
    }

    return {
      icon: <Bell className="h-5 w-5 text-gray-500" />,
      text: 'Aguardando Configuração',
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200'
    };
  };

  const statusInfo = getStatusInfo();

  return (
    <div className={`p-4 rounded-lg border ${statusInfo.bgColor} ${statusInfo.borderColor} ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {statusInfo.icon}
          <div>
            <p className={`font-medium ${statusInfo.color}`}>
              {statusInfo.text}
            </p>
            {showDetails && (
              <div className="text-sm text-gray-600 mt-1">
                <p>Navegador: {isSupported ? 'Suportado' : 'Não Suportado'}</p>
                <p>Push: {isPushSupported ? 'Suportado' : 'Não Suportado'}</p>
                <p>Permissão: {permission}</p>
                <p>Assinatura: {hasActiveSubscription ? 'Ativa' : 'Inativa'}</p>
                {pushSubscription && (
                  <p className="text-xs text-gray-500 mt-1">
                    Endpoint: {pushSubscription.endpoint.substring(0, 50)}...
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-white/50 rounded-lg transition-colors disabled:opacity-50"
          title="Atualizar status"
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>
      
      {needsAttention && (
        <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
          <p className="font-medium">Ação Necessária:</p>
          <p>
            {permission !== 'granted' 
              ? 'Ative as notificações nas configurações do navegador para receber alertas importantes.'
              : 'Configure as notificações push para receber alertas em tempo real.'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default PushNotificationStatus;
